/**
 * Who is signed in, and what they can do about it.
 *
 * The identity had been five badges and two labelled buttons across the top of
 * every page. None of it changes during a session, so it belongs behind the
 * control that names the person rather than in front of the content.
 *
 * @module components/shell/UserMenu
 */

import React from "react";

import { roleLabel } from "@/components/shared/role-labels";
import { Avatar, Badge, Divider, Dropdown, Heading, type MenuItem, Text } from "@/components/ui";

export interface UserMenuProps {
  /** The person's name, or their email where they have not set one. */
  displayName: string;
  email: string;
  /** Their account role slug, shown by its label. */
  accountRole: string;
  /** Shown only where it is not implied by the account role. */
  programmeCreator?: boolean;
  onOpenSettings: () => void;
  onSwitchUser: () => void;
  onLogout: () => void;
  /** Appended for a development build: impersonation, the mode switch. */
  extraItems?: readonly MenuItem[];
}

/**
 * @example
 * ```tsx
 * <UserMenu displayName={name} email={email} accountRole={role} … />
 * ```
 */
export function UserMenu({
  displayName,
  email,
  accountRole,
  programmeCreator = false,
  onOpenSettings,
  onSwitchUser,
  onLogout,
  extraItems = [],
}: UserMenuProps): React.ReactElement {
  const items: MenuItem[] = [
    {
      key: "settings",
      label: "Settings",
      icon: "gear",
      onSelect: onOpenSettings,
      "data-testid": "open-settings",
    },
    ...extraItems,
    {
      key: "switch-user",
      label: "Switch user",
      icon: "user-switch",
      onSelect: onSwitchUser,
      "data-testid": "switch-user",
    },
    {
      key: "log-out",
      label: "Log out",
      icon: "sign-out",
      onSelect: onLogout,
      "data-testid": "log-out",
    },
  ];

  return (
    <Dropdown
      aria-label={`Signed in as ${displayName}`}
      data-testid="user-menu"
      trigger={<Avatar name={displayName} size="sm" />}
      triggerAppearance="ghost-circle"
      items={items}
      header={
        <div>
          <div className="px-3 py-2">
            <Heading level={4} className="truncate" data-testid="signed-in-user">
              {displayName}
            </Heading>
            <Text tone="muted" className="truncate">
              {email}
            </Text>
            <div className="mt-2 flex flex-wrap gap-1">
              <Badge tone="neutral" size="xs">
                {roleLabel(accountRole)}
              </Badge>
              {programmeCreator && (
                <Badge tone="primary" size="xs">
                  Programme creator
                </Badge>
              )}
            </div>
          </div>
          <Divider />
        </div>
      }
    />
  );
}
